<!-- EmployeesManagementTable.vue -->
<!-- Componente para gestión de empleadas del grupo -->

<template>
  <div class="employees-management-table">
    <!-- Resumen de empleadas -->
    <v-row class="mb-4">
      <v-col cols="12" md="4" v-for="stat in employeeStats" :key="stat.label">
        <v-card :color="stat.color" variant="tonal">
          <v-card-text class="text-center">
            <div class="text-h4">{{ stat.value }}</div>
            <div class="text-subtitle-2">{{ stat.label }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla de empleadas -->
    <v-data-table
      :headers="employeeHeaders"
      :items="employees"
      :loading="loading"
      class="elevation-1"
      :items-per-page="10"
    >
      <template #top>
        <v-toolbar flat>
          <v-toolbar-title>Empleadas del Grupo</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showAddEmployeeDialog = true">
            <v-icon start>mdi-account-plus</v-icon>
            Añadir Empleada
          </v-btn>
        </v-toolbar>
      </template>

      <template #item.name="{ item }">
        <div class="d-flex align-center">
          <v-avatar size="32" class="me-2">
            <v-icon icon="mdi-account" />
          </v-avatar>
          <div>
            <div class="font-weight-bold">{{ item.name }}</div>
            <div class="text-caption text-medium-emphasis">{{ item.position }}</div>
          </div>
        </div>
      </template>

      <template #item.status="{ item }">
        <v-chip
          :color="getStatusColor(item.status)"
          size="small"
          variant="tonal"
        >
          {{ getStatusLabel(item.status) }}
        </v-chip>
      </template>

      <template #item.performance="{ item }">
        <div class="d-flex align-center">
          <v-progress-circular
            :model-value="item.performance || 0"
            size="24"
            width="3"
            :color="getPerformanceColor(item.performance)"
          />
          <span class="ml-2">{{ item.performance || 0 }}%</span>
        </div>
      </template>

      <template #item.schedule="{ item }">
        <v-chip
          color="info"
          size="small"
          variant="outlined"
          @click="$emit('manage-schedule', item.id, item.schedule)"
        >
          {{ item.schedule }}
          <v-icon end size="16">mdi-pencil</v-icon>
        </v-chip>
      </template>

      <template #item.agency="{ item }">
        <span v-if="item.agency" class="text-primary">{{ item.agency }}</span>
        <span v-else class="text-medium-emphasis">Sin asignar</span>
      </template>

      <template #item.actions="{ item }">
        <v-btn-group variant="text" size="small">
          <v-btn
            icon="mdi-eye"
            @click="$emit('view-employee', item)"
          />
          <v-btn
            icon="mdi-calendar-edit"
            @click="$emit('manage-schedule', item.id, item.schedule)"
          />
          <v-menu>
            <template #activator="{ props }">
              <v-btn
                icon="mdi-dots-vertical"
                v-bind="props"
              />
            </template>
            <v-list>
              <v-list-item @click="assignToAgency(item)">
                <template #prepend>
                  <v-icon icon="mdi-office-building" />
                </template>
                <v-list-item-title>Asignar Agencia</v-list-item-title>
              </v-list-item>
              <v-list-item @click="viewPerformance(item)">
                <template #prepend>
                  <v-icon icon="mdi-chart-line" />
                </template>
                <v-list-item-title>Ver Rendimiento</v-list-item-title>
              </v-list-item>
              <v-list-item @click="managePermissions(item)">
                <template #prepend>
                  <v-icon icon="mdi-shield-account" />
                </template>
                <v-list-item-title>Gestionar Permisos</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-btn-group>
      </template>
    </v-data-table>

    <!-- Dialog para añadir empleada -->
    <v-dialog v-model="showAddEmployeeDialog" max-width="600px">
      <v-card>
        <v-card-title>Añadir Nueva Empleada</v-card-title>
        <v-card-text>
          <v-form ref="employeeForm" v-model="formValid">
            <v-text-field
              v-model="newEmployee.name"
              label="Nombre Completo"
              :rules="[rules.required]"
              required
            />
            <v-text-field
              v-model="newEmployee.position"
              label="Posición"
              :rules="[rules.required]"
              required
            />
            <v-select
              v-model="newEmployee.schedule"
              :items="scheduleOptions"
              label="Horario"
              :rules="[rules.required]"
              required
            />
            <v-select
              v-model="newEmployee.agency"
              :items="availableAgencies"
              item-title="name"
              item-value="name"
              label="Agencia (Opcional)"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showAddEmployeeDialog = false">Cancelar</v-btn>
          <v-btn 
            color="primary" 
            @click="addEmployee"
            :disabled="!formValid"
          >
            Añadir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de asignación de agencia -->
    <v-dialog v-model="showAssignDialog" max-width="400px">
      <v-card>
        <v-card-title>Asignar Agencia</v-card-title>
        <v-card-text>
          <v-select
            v-model="assignmentAgency"
            :items="availableAgencies"
            item-title="name"
            item-value="name"
            label="Seleccionar Agencia"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showAssignDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="confirmAssignment">Asignar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineEmits } from 'vue'

interface Employee {
  id: number
  name: string
  position: string
  schedule: string
  status: 'active' | 'inactive' | 'vacation'
  agency?: string
  performance?: number
}

interface Props {
  employees: Employee[]
  loading?: boolean
  availableAgencies?: any[]
}

const props = defineProps<Props>()
const emit = defineEmits(['view-employee', 'manage-schedule', 'add-employee'])

const showAddEmployeeDialog = ref(false)
const showAssignDialog = ref(false)
const formValid = ref(false)
const selectedEmployee = ref<Employee | null>(null)
const assignmentAgency = ref('')

const newEmployee = ref({
  name: '',
  position: '',
  schedule: '',
  agency: ''
})

const rules = {
  required: (value: string) => !!value || 'Este campo es requerido'
}

const scheduleOptions = [
  'Mañana (7:00-15:00)',
  'Tarde (15:00-23:00)',
  'Noche (23:00-7:00)',
  'Tiempo Completo (7:00-19:00)',
  'Medio Tiempo (9:00-13:00)'
]

const employeeHeaders = computed(() => [
  { title: 'Empleada', key: 'name', sortable: true },
  { title: 'Estado', key: 'status' },
  { title: 'Rendimiento', key: 'performance', sortable: true },
  { title: 'Horario', key: 'schedule' },
  { title: 'Agencia', key: 'agency' },
  { title: 'Acciones', key: 'actions', sortable: false }
])

const employeeStats = computed(() => {
  const total = props.employees.length
  const active = props.employees.filter(e => e.status === 'active').length
  const avgPerformance = total > 0 
    ? Math.round(props.employees.reduce((sum, e) => sum + (e.performance || 0), 0) / total)
    : 0

  return [
    { label: 'Total Empleadas', value: total, color: 'primary' },
    { label: 'Activas', value: active, color: 'success' },
    { label: 'Rendimiento Promedio', value: `${avgPerformance}%`, color: 'info' }
  ]
})

const getStatusColor = (status: string) => {
  const colors: { [key: string]: string } = {
    active: 'success',
    inactive: 'error',
    vacation: 'warning'
  }
  return colors[status] || 'default'
}

const getStatusLabel = (status: string) => {
  const labels: { [key: string]: string } = {
    active: 'Activa',
    inactive: 'Inactiva',
    vacation: 'Vacaciones'
  }
  return labels[status] || status
}

const getPerformanceColor = (performance?: number) => {
  if (!performance) return 'grey'
  if (performance >= 90) return 'success'
  if (performance >= 70) return 'warning'
  return 'error'
}

const addEmployee = async () => {
  const employeeData = {
    ...newEmployee.value,
    status: 'active',
    performance: 85 // Valor inicial
  }
  
  emit('add-employee', employeeData)
  
  // Reset form
  newEmployee.value = {
    name: '',
    position: '',
    schedule: '',
    agency: ''
  }
  showAddEmployeeDialog.value = false
}

const assignToAgency = (employee: Employee) => {
  selectedEmployee.value = employee
  assignmentAgency.value = employee.agency || ''
  showAssignDialog.value = true
}

const confirmAssignment = () => {
  if (selectedEmployee.value) {
    // Emit assignment event
    console.log('Asignando', selectedEmployee.value.name, 'a', assignmentAgency.value)
    showAssignDialog.value = false
  }
}

const viewPerformance = (employee: Employee) => {
  console.log('Ver rendimiento de:', employee.name)
  // Implement performance view
}

const managePermissions = (employee: Employee) => {
  console.log('Gestionar permisos de:', employee.name)
  // Implement permissions management
}
</script>

<style scoped>
.employees-management-table {
  width: 100%;
}

.text-primary {
  color: rgb(33, 150, 243);
}

.text-medium-emphasis {
  color: rgba(0, 0, 0, 0.6);
}
</style>
